import type {
  Account,
  AccountList,
  Client,
  DayMetrics,
  DayRuns,
  MetricsRange,
  Producer,
  Property,
  Quote,
  QuoteDocument,
  QuoteDocumentDownload,
  QuoteInputs,
  QuoteRun,
  QuoteShare,
  QuoteStage,
  QuoteStatus,
  RecordingDownload,
  RunDetail,
  RunState,
  RunTiming,
  TranscriptPage,
  WindowMetrics,
} from "@iqa/contracts";
import { RUN_STATE_FIELDS } from "@iqa/contracts/live-fields";
import type { Role } from "./auth.ts";

/**
 * The data API as the portal uses it (docs/data-model.md#data-api): GraphQL over HTTP with the
 * signed-in user's id token. The pages take a `PortalApi`, so tests give them a fake.
 */

export type {
  Account,
  AccountList,
  Client,
  DayMetrics,
  DayRuns,
  MetricsRange,
  Producer,
  Property,
  Quote,
  QuoteDocument,
  QuoteDocumentDownload,
  QuoteInputs,
  QuoteRun,
  QuoteShare,
  RecordingDownload,
  RunDetail,
  RunState,
  RunTiming,
  TranscriptPage,
  WindowMetrics,
};

/** A quote with what it links to, as the quote list and a quote's page show it. */
export interface QuoteEntry {
  readonly quote: Quote;
  readonly clients: readonly Client[];
  readonly properties: readonly Property[];
  /** The producer assigned it; absent for a quote made before that was recorded. */
  readonly producer?: Producer;
  readonly runs: readonly QuoteRun[];
}

export interface QuoteListPage {
  readonly quotes: readonly QuoteEntry[];
  /** Pass as `before` for the next page; absent when there are no more. */
  readonly next?: string;
}

export interface QuoteFilter {
  readonly stage?: QuoteStage;
  readonly status?: QuoteStatus;
  /** Only the caller's own quotes. */
  readonly mine?: boolean;
}

/** A client found by a search, with the properties they own. */
export interface ClientMatch {
  readonly client: Client;
  readonly properties: readonly Property[];
}

/** A client with their properties and the quotes made for them. */
export interface ClientEntry {
  readonly client: Client;
  readonly properties: readonly Property[];
  /** Newest first. */
  readonly quotes: readonly Quote[];
}

/** The run fields the run browser selects; the contracts' entity type keeps unknown fields too. */
export interface RunFields {
  readonly id: string;
  readonly quoteId: string;
  readonly carrier?: string;
  readonly status?: string;
  readonly premium?: number;
  readonly carrierQuoteNumber?: string;
  readonly lastUrl?: string;
  readonly lastPageTitle?: string;
  readonly createdAt: string;
  readonly startedAt?: string;
  readonly endedAt?: string;
  readonly firstQuotedAt?: string;
  readonly timing?: RunTiming;
}

export interface PortalApi {
  /** The metrics of the local days from `from` to `to`, in an IANA time zone. */
  metrics(from: string, to: string, timeZone: string): Promise<MetricsRange>;
  accounts(): Promise<AccountList>;
  createAccount(email: string, role: Role): Promise<Account>;
  setAccountRole(email: string, role: Role): Promise<Account>;
  setAccountEnabled(email: string, enabled: boolean): Promise<Account>;
  // The run browser (operators only)
  /** The runs of a day: from its start (included) to its end (left out), ISO instants. */
  runsOfDay(from: string, to: string): Promise<DayRuns>;
  runDetail(runId: string): Promise<RunDetail>;
  sessionTranscript(sessionId: string, offset?: number): Promise<TranscriptPage>;
  recordingDownload(runId: string, segmentId: string): Promise<RecordingDownload>;
  /** A run's live state, for polling when the subscription is unavailable. */
  runState(runId: string): Promise<RunState | undefined>;
  // Quotes and clients (members and up)
  /** A page of every quote, newest first; `before` is the previous page's `next`. */
  listQuotes(filter: QuoteFilter, before?: string): Promise<QuoteListPage>;
  quote(quoteId: string): Promise<QuoteEntry>;
  /** Clients by name, phone or email: a text with an `@` is an email, one of digits a phone. */
  findClients(text: string): Promise<ClientMatch[]>;
  client(clientId: string): Promise<ClientEntry>;
  // Quote documents: the carriers' proposals, kept 90 days
  quoteDocuments(quoteId: string): Promise<QuoteDocument[]>;
  documentDownload(documentId: string): Promise<QuoteDocumentDownload>;
  // Share links: a page for the client, current policy against the quote
  quoteShares(quoteId: string): Promise<QuoteShare[]>;
  /** Shares the runs named (every quoted run without them) for some days (30 without them). */
  shareQuote(quoteId: string, runIds?: readonly string[], days?: number): Promise<QuoteShare>;
  revokeShare(token: string): Promise<QuoteShare>;
}

/** An error the API answered with; its message starts with the code (`FORBIDDEN: …`). */
export class ApiError extends Error {
  override readonly name = "ApiError";
}

/** The longest a request may take. */
const REQUEST_MS = 15_000;

export type Request = <T>(query: string, variables?: Record<string, unknown>) => Promise<T>;

/** A GraphQL request function for an endpoint; `token` gives the id token for each call. */
export function graphqlRequest(
  apiUrl: string,
  token: () => Promise<string | undefined>,
  fetchImpl: typeof fetch = (...args) => fetch(...args),
): Request {
  return async <T>(query: string, variables: Record<string, unknown> = {}) => {
    const idToken = await token();
    if (!idToken) throw new ApiError("UNAUTHORIZED: You are signed out. Sign in again.");
    const response = await fetchImpl(apiUrl, {
      method: "POST",
      // AppSync takes the Cognito id token as it is, without `Bearer`.
      headers: { "content-type": "application/json", authorization: idToken },
      body: JSON.stringify({ query, variables }),
      signal: AbortSignal.timeout(REQUEST_MS),
    });
    const body = (await response.json().catch(() => undefined)) as
      | { data?: T; errors?: { message?: string }[] }
      | undefined;
    const message = body?.errors?.map((error) => error.message).join("; ");
    if (message) throw new ApiError(message);
    if (!response.ok || body?.data === undefined) {
      throw new ApiError(`The data API answered HTTP ${response.status}`);
    }
    return body.data;
  };
}

const SEGMENT_GAPS = "count omitted segments { runId segmentId producerId endedAt }";

const WINDOW_FIELDS = `
  start end truncated
  completeness {
    percent complete units segments pending open invalidRecordings
    withoutRecording { ${SEGMENT_GAPS} }
    withoutSession { ${SEGMENT_GAPS} }
    runsWithoutSegment { count omitted runs { runId sessionIds firstNamedAt } }
  }
  dailyUse { producers sessions omitted byProducer { producerId email sessions sessionIds } }
  interventions { runs messages perRun unmeasuredSegments omitted byRun { runId segments messages } }
  timing {
    runs pending medianElapsedSeconds meanElapsedSeconds medianActiveSeconds meanActiveSeconds
    omitted
    byRun {
      runId carrier status startedAt endedAt elapsedSeconds activeSeconds segments openSegments
    }
  }
`;

export const METRICS_FIELDS = `
  timeZone from to evaluatedAt cutoff truncated
  days { date ${WINDOW_FIELDS} }
  total { ${WINDOW_FIELDS} }
`;

const ACCOUNT_FIELDS = "email role roles enabled status createdAt manageable";

const RUN_FIELDS = `
  id quoteId carrier status premium carrierQuoteNumber lastUrl lastPageTitle createdAt updatedAt
  startedAt endedAt firstQuotedAt
  timing { startedAt endedAt elapsedSeconds activeSeconds segments openSegments sessions asOf }
`;

const SEGMENT_FIELDS = `
  runId segmentId startedAt endedAt producerId sessionId
  recording recordingBytes recordingArrivedAt recordingProblem
  recordingSummary {
    formatVersion start end daemonVersion calls network downloads screenshots bodies
    pendingCalls incompleteNetwork dropped betweenSegments packageCapReached
  }
`;

const LINE_FIELDS = `
  offset bytes type uuid timestamp prompt sidechain text thinking cut problem
  toolCalls { id name input }
  toolResults { toolUseId error text }
`;

const ENTITY_FIELDS = "id createdAt updatedAt revision";
const ADDRESS_FIELDS = "address { street unit city state zip county }";
const QUOTE_FIELDS = `${ENTITY_FIELDS} lines effectiveDate dwellingCoverage deductible liabilityLimit premiumPayer status stage`;
const CLIENT_FIELDS = `${ENTITY_FIELDS} kind firstName lastName businessName contactName phones emails`;
const PROPERTY_FIELDS = `${ENTITY_FIELDS} ${ADDRESS_FIELDS} occupancy yearBuilt priorCarrier priorPolicyExpiration priorPremium`;
const PRODUCER_FIELDS = `${ENTITY_FIELDS} email role`;
const QUOTE_RUN_FIELDS = "id carrier status premium carrierQuoteNumber lastUrl startedAt endedAt";
const SHARE_FIELDS = `
  token url quoteId createdAt createdBy expiresAt revokedAt viewedAt lastViewedAt views
  snapshot {
    greeting address stage effectiveDate dwellingCoverage deductible liabilityLimit producerEmail
    current { carrier premium expires }
    offers { carrier premium carrierQuoteNumber }
  }
`;

/** A linked entity of `getContext`, as the quote's and the client's pages read it. */
interface ContextLink {
  readonly link?: { readonly relation?: string };
  readonly direction?: "in" | "out";
  readonly entity?: { readonly __typename?: string } & Record<string, unknown>;
}

interface ContextAnswer {
  readonly entity?: Record<string, unknown>;
  readonly links?: readonly ContextLink[];
  readonly runs?: readonly QuoteRun[];
}

/** The entities a context links to by one relation and of one GraphQL type. */
function linked<T>(context: ContextAnswer, relation: string, typename: string): T[] {
  return (context.links ?? []).flatMap((view) =>
    view.link?.relation === relation && view.entity?.__typename === typename
      ? [withoutTypename(view.entity) as T]
      : [],
  );
}

function withoutTypename(entity: Record<string, unknown>): Record<string, unknown> {
  const { __typename: _, ...fields } = entity;
  return fields;
}

/** A quote's inputs: AWSJSON, which AppSync sends as JSON text. */
function quoteOf(entity: Record<string, unknown> | undefined): Quote {
  const fields = withoutTypename(entity ?? {});
  if (typeof fields.inputs === "string") fields.inputs = JSON.parse(fields.inputs) as QuoteInputs;
  return fields as Quote;
}

/** The kind of a client search, from what was typed. */
export function searchOf(text: string): { name?: string; phone?: string; email?: string } {
  const trimmed = text.trim();
  if (trimmed.includes("@")) return { email: trimmed };
  if (/^[+\d\s().-]+$/.test(trimmed) && /\d{3}/.test(trimmed)) return { phone: trimmed };
  return { name: trimmed };
}

/** GraphQL leaves an absent optional field `null`; the contracts' types leave it out. */
function withoutNulls<T>(value: T): T {
  if (Array.isArray(value)) return value.map(withoutNulls) as T;
  if (typeof value !== "object" || value === null) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, field]) => field !== null)
      .map(([name, field]) => [name, withoutNulls(field)]),
  ) as T;
}

export function portalApi(request: Request): PortalApi {
  const call = async <T>(query: string, variables?: Record<string, unknown>) =>
    withoutNulls(await request<T>(query, variables));
  return {
    metrics: async (from, to, timeZone) =>
      (
        await call<{ metrics: MetricsRange }>(
          `query Metrics($from: AWSDate!, $to: AWSDate!, $timeZone: String!) {
            metrics(from: $from, to: $to, timeZone: $timeZone) { ${METRICS_FIELDS} }
          }`,
          { from, to, timeZone },
        )
      ).metrics,
    accounts: async () =>
      (
        await call<{ accounts: AccountList }>(
          `query Accounts { accounts { truncated accounts { ${ACCOUNT_FIELDS} } } }`,
        )
      ).accounts,
    createAccount: async (email, role) =>
      (
        await call<{ createAccount: Account }>(
          `mutation Create($email: String!, $role: ProducerRole!) {
            createAccount(email: $email, role: $role) { ${ACCOUNT_FIELDS} }
          }`,
          { email, role },
        )
      ).createAccount,
    setAccountRole: async (email, role) =>
      (
        await call<{ setAccountRole: Account }>(
          `mutation SetRole($email: String!, $role: ProducerRole!) {
            setAccountRole(email: $email, role: $role) { ${ACCOUNT_FIELDS} }
          }`,
          { email, role },
        )
      ).setAccountRole,
    setAccountEnabled: async (email, enabled) =>
      (
        await call<{ setAccountEnabled: Account }>(
          `mutation SetEnabled($email: String!, $enabled: Boolean!) {
            setAccountEnabled(email: $email, enabled: $enabled) { ${ACCOUNT_FIELDS} }
          }`,
          { email, enabled },
        )
      ).setAccountEnabled,
    runsOfDay: async (from, to) =>
      (
        await call<{ runsOfDay: DayRuns }>(
          `query RunsOfDay($from: AWSDateTime!, $to: AWSDateTime!) {
            runsOfDay(from: $from, to: $to) {
              from to omitted truncated
              runs {
                runId segments open recordings joined producerIds firstStartedAt lastEndedAt
                run { ${RUN_FIELDS} }
                live { ${RUN_STATE_FIELDS} }
              }
            }
          }`,
          { from, to },
        )
      ).runsOfDay,
    runDetail: async (runId) =>
      (
        await call<{ runDetail: RunDetail }>(
          `query RunDetail($runId: ID!) {
            runDetail(runId: $runId) {
              run { ${RUN_FIELDS} }
              quote { id status createdAt }
              segments { ${SEGMENT_FIELDS} }
              sessions {
                sessionId producerId startedRun spans { from to }
                session { id firstActivityAt lastActivityAt batchCount lineCount }
              }
              omittedSessions
              live { ${RUN_STATE_FIELDS} }
            }
          }`,
          { runId },
        )
      ).runDetail,
    runState: async (runId) =>
      (
        await call<{ runState?: RunState }>(
          `query RunState($runId: ID!) { runState(runId: $runId) { ${RUN_STATE_FIELDS} } }`,
          { runId },
        )
      ).runState,
    sessionTranscript: async (sessionId, offset) =>
      (
        await call<{ sessionTranscript: TranscriptPage }>(
          `query Transcript($sessionId: ID!, $offset: Int) {
            sessionTranscript(sessionId: $sessionId, offset: $offset) {
              sessionId offset nextOffset endOffset batches missing { from to }
              lines { ${LINE_FIELDS} }
            }
          }`,
          { sessionId, offset: offset ?? 0 },
        )
      ).sessionTranscript,
    recordingDownload: async (runId, segmentId) =>
      (
        await call<{ recordingDownload: RecordingDownload }>(
          `query Download($runId: ID!, $segmentId: ID!) {
            recordingDownload(runId: $runId, segmentId: $segmentId) { url key bytes expiresAt }
          }`,
          { runId, segmentId },
        )
      ).recordingDownload,
    listQuotes: async (filter, before) => {
      const { listQuotes } = await call<{ listQuotes?: QuoteListPage }>(
        `query ListQuotes($stage: QuoteStage, $status: QuoteStatus, $mine: Boolean, $before: String) {
          listQuotes(stage: $stage, status: $status, mine: $mine, before: $before) {
            next
            quotes {
              quote { ${QUOTE_FIELDS} inputs }
              clients { ${CLIENT_FIELDS} }
              properties { ${PROPERTY_FIELDS} }
              producer { ${PRODUCER_FIELDS} }
              runs { ${QUOTE_RUN_FIELDS} }
            }
          }
        }`,
        { ...filter, ...(before !== undefined && { before }) },
      );
      return {
        ...listQuotes,
        quotes: (listQuotes?.quotes ?? []).map((entry) => ({
          ...entry,
          quote: quoteOf(entry.quote),
        })),
      };
    },
    quote: async (quoteId) => {
      const { getContext = {} } = await call<{ getContext?: ContextAnswer }>(
        `query Quote($quoteId: ID!) {
          getContext(quoteId: $quoteId) {
            entity { ... on Quote { ${QUOTE_FIELDS} inputs } }
            links {
              link { relation }
              direction
              entity {
                __typename
                ... on Client { ${CLIENT_FIELDS} }
                ... on Property { ${PROPERTY_FIELDS} }
                ... on Producer { ${PRODUCER_FIELDS} }
              }
            }
            runs { ${QUOTE_RUN_FIELDS} }
          }
        }`,
        { quoteId },
      );
      const producer = linked<Producer>(getContext, "assigned", "Producer")[0];
      return {
        quote: quoteOf(getContext.entity),
        clients: linked<Client>(getContext, "for", "Client"),
        properties: linked<Property>(getContext, "covers", "Property"),
        ...(producer && { producer }),
        runs: getContext.runs ?? [],
      };
    },
    findClients: async (text) =>
      (
        await call<{ findClients: ClientMatch[] }>(
          `query FindClients($name: String, $phone: String, $email: String) {
            findClients(name: $name, phone: $phone, email: $email) {
              client { ${CLIENT_FIELDS} }
              properties { ${PROPERTY_FIELDS} }
            }
          }`,
          searchOf(text),
        )
      ).findClients,
    client: async (clientId) => {
      const { getContext = {} } = await call<{ getContext?: ContextAnswer }>(
        `query Client($clientId: ID!) {
          getContext(clientId: $clientId) {
            entity { ... on Client { ${CLIENT_FIELDS} } }
            links {
              link { relation }
              direction
              entity {
                __typename
                ... on Property { ${PROPERTY_FIELDS} }
                ... on Quote { ${QUOTE_FIELDS} }
              }
            }
          }
        }`,
        { clientId },
      );
      const quotes = linked<Record<string, unknown>>(getContext, "for", "Quote").map(quoteOf);
      return {
        client: withoutTypename(getContext.entity ?? {}) as Client,
        properties: linked<Property>(getContext, "owns", "Property"),
        quotes: quotes.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "")),
      };
    },
    quoteDocuments: async (quoteId) =>
      (
        await call<{ quoteDocuments: QuoteDocument[] }>(
          `query QuoteDocuments($quoteId: ID!) {
            quoteDocuments(quoteId: $quoteId) {
              documentId quoteId runId carrier name bytes state createdAt createdBy storedAt expiresAt
            }
          }`,
          { quoteId },
        )
      ).quoteDocuments,
    documentDownload: async (documentId) =>
      (
        await call<{ quoteDocumentDownload: QuoteDocumentDownload }>(
          `query DocumentDownload($documentId: ID!) {
            quoteDocumentDownload(documentId: $documentId) { url expiresAt }
          }`,
          { documentId },
        )
      ).quoteDocumentDownload,
    quoteShares: async (quoteId) =>
      (
        await call<{ quoteShares: QuoteShare[] }>(
          `query QuoteShares($quoteId: ID!) { quoteShares(quoteId: $quoteId) { ${SHARE_FIELDS} } }`,
          { quoteId },
        )
      ).quoteShares,
    shareQuote: async (quoteId, runIds, days) =>
      (
        await call<{ shareQuote: QuoteShare }>(
          `mutation ShareQuote($quoteId: ID!, $runIds: [ID!], $expiresInDays: Int) {
            shareQuote(quoteId: $quoteId, runIds: $runIds, expiresInDays: $expiresInDays) {
              ${SHARE_FIELDS}
            }
          }`,
          {
            quoteId,
            ...(runIds !== undefined && { runIds }),
            ...(days !== undefined && { expiresInDays: days }),
          },
        )
      ).shareQuote,
    revokeShare: async (token) =>
      (
        await call<{ revokeShare: QuoteShare }>(
          `mutation RevokeShare($token: String!) { revokeShare(token: $token) { ${SHARE_FIELDS} } }`,
          { token },
        )
      ).revokeShare,
  };
}
